<template>
  <div :class="`${addEditClass(this.mapping.lookups.idField)} form-field form-group autocomplete flex-group`">
    <label v-if="!removeLabelClass" class="k-textbox-label" :for="this.mapping.lookups.idField">
      <span></span>
      <slot></slot>
    </label>
    <div class="flex-autocomplete-field">
      <div :class="['autocomplete-text', isWater || clearRightPadding ? 'clear-right-padding' : '']">
        <multiselect
          v-model="selectedItem"
          :allowEmpty="allowEmpty"
          :label="labelField"
          :placeholder="placeholder"
          :preserve-search="preserveSearch"
          :openDirection="openDirection"
          :options="prependBlankOption(dataSource)"
          :optionsLimit="removeOptionsLimit ? 1000 : 25"
          :show-labels="false"
          :tabindex="parseInt(tabindex)"
          :track-by="trackBy"
          :ref="uniqueRef"
          :internal-search="!isServerSide"
          :disabled="disabled"
          :searchOnOpen="searchOnOpen"
          @search-change="onSearchChange"
          @open="scrollToSelectedOption"
          @close="checkForEmptyOption"
          @select="checkForEmptyOption"
          @remove="checkForEmptyOption"
        >
          <span slot="singleLabel" v-if="!hasDescription">{{ formData[mapping.formProps.codeField] }}</span>
          <div slot="singleLabel" v-else>
            {{ formData[mapping.formProps.nameField] }}
            <span v-if="store === 'FluidBaselineSample' && formData.fluidBaselineSampleDate">
              ({{ formatDate(formData.fluidBaselineSampleDate) }})
            </span>
          </div>
          <span slot="option" slot-scope="{ option }">
            <span v-if="option.isNewOption" class="create-new-option">
              {{ createNewItemText }}: "{{ option[optionProp] }}"
            </span>
            <span v-else>
              {{ option[optionProp] }}
            </span>
            <!-- ideally we would create a named slot for this -->
            <span v-if="option.customerCode && store === 'Customer'" class="bold">{{ option.customerCode }}</span>
            <span v-if="option.customerName && store === 'Customer'">- {{ option.customerName }}</span>
            <span v-if="option.attention && store === 'Customer'">(attn:{{ option.attention }})</span>
            <span v-if="option.address && store === 'Customer'">{{ option.address }}</span>
            <span v-if="option.email && store === 'Customer'">Email: {{ option.email }}</span>
            <span v-if="store === 'FluidBaselineSample' && option.fluidBaselineSampleDate">
              ({{ formatDate(option.fluidBaselineSampleDate) }})</span
            >
          </span>
        </multiselect>
      </div>
      <div v-if="linkToItem.id" class="auto-complete-link-to-item">
        <a :href="`${getLinkToUrl}`" target="_blank" rel="noopener noreferrer">
          {{ linkToItem.label }}<i class="fas fa-external-link-alt"></i>
        </a>
      </div>
      <div v-if="hasError" class="autocomplete-error">
        {{ this.errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import AjaxDataService from '../../../services/ajax.service';
import AutoCompleteMixin from './AutoCompleteMixin';
import SampleMixin from './SampleMixin';
import GlobalStore from '../../../store';
import Constants from '../../constants/constants';
import debounce from 'lodash.debounce';

export default {
  name: 'AutoComplete',
  data() {
    return {
      globalStore: GlobalStore,
      uniqueRef: `multiselect-${Math.random()}`,
      emptyItem: 'None',
      isLoaded: false,
      hasError: false,
      errorMessage: '',
      preserveSearch: false
    };
  },
  created() {
    this.preserveSearch = false;
    this.onSearchChange = debounce(e => {
      this.preserveSearch = true;
      this.searchChange(e);
    }, Constants.Api.debounce.autocomplete);
  },
  mounted() {
    this.$refs[this.uniqueRef]?.$el.addEventListener('keydown', event => this.selectMultiselectOption(event));
  },
  destroyed() {
    this.$refs[this.uniqueRef]?.$el.removeEventListener('keydown', event => this.selectMultiselectOption(event));
  },
  computed: {
    getLinkToUrl() {
      if (!this.linkToItem?.location || !this.linkToItem?.id) return '';

      const currentUrl = new URL(window.location.href);
      const basePath = currentUrl.pathname.split('/detail')[0];
      if (currentUrl.pathname.includes('/detail')) {
        return `${currentUrl.origin}${basePath}/detail?id=${this.linkToItem.id}`;
      } else {
        return `${currentUrl.origin}${basePath.replace('/fluids', '')}/${this.linkToItem.location}/detail?id=${
          this.linkToItem.id
        }`;
      }
    },
    isServerSide() {
      return this.mapping.isServerSide === undefined ? false : this.mapping.isServerSide;
    },
    dataSource() {
      const typeCode = this.mapping?.typeCode;
      const dataSource = this.globalStore.state.AutoCompletes[this.store].dataSource;

      if (!dataSource?.length) {
        return [];
      }

      if (typeCode && typeCode !== '') {
        return dataSource.filter(item => item.typeCode === typeCode);
      }
      return dataSource;
    },
    optionProp() {
      return this.mapping.optionLabelProp === undefined ? this.mapping.lookups.nameField : this.mapping.optionLabelProp;
    },
    labelField() {
      const { lookups } = this.mapping;
      return lookups.searchField === undefined ? lookups.codeField : lookups.searchField;
    },
    trackBy() {
      const { lookups } = this.mapping;
      return lookups.nameField === undefined
        ? lookups.codeField === undefined
          ? lookups.idField
          : lookups.codeField
        : lookups.nameField;
    },
    selectedItem: {
      get() {
        const item = this.getSelectedItem();

        this.$emit('loadComplete', item);

        return item;
      },
      async set(newValue) {
        if (newValue && newValue.isNewOption) {
          this.$emit('create-new-item', newValue[this.labelField], this.mapping.lookups.idField);
          return;
        }
        if (this.store === 'Fluid' && newValue.fluidID && (this.formData.oilSampleID || this.formData.coolantSampleID)) {
          this.$emit('fluid-selected', newValue.fluidID);
        }
        this.CheckForThirdPartyError(newValue);
        this.setSelectedItem(newValue);
        this.$emit('update', newValue);
      }
    },
    selectedID() {
      return this.formData[this.mapping.formProps.idField];
    }
  },
  mixins: [AutoCompleteMixin],
  props: {
    allowCreateNew: {
      type: Boolean,
      default: false
    },
    createNewItemText: {
      type: String,
      default: 'Add new item'
    },
    fluidInfo: {
      type: Object,
      default: () => ({})
    },
    linkToItem: {
      type: Object,
      default: () => ({})
    },
    searchOnOpen: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    // TODO: Should clean this up into it's own file for only the customer third party field
    async CheckForThirdPartyError(newValue) {
      if (!this.$el.baseURI.includes('/customers')) return;
      if (this.mapping.lookups.idField !== 'thirdPartyName') return;

      let oldThirdParty = this.selectedItem.thirdPartyName;
      let newThirdParty = newValue.thirdPartyName;

      if (!oldThirdParty) return;

      const serviceData = await AjaxDataService.getData(Constants.Api.Endpoint.ThirdParty.thatWouldBeAffected, {
        oldThirdParty,
        newThirdParty
      });

      const numberAffected = serviceData.data;

      if (numberAffected > 0) {
        this.hasError = true;
        this.errorMessage = `Confirming this third party change will affect ${numberAffected} User(s)`;
      } else {
        this.hasError = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toISOString().slice(0, 10);
    },
    clearPreserveSearch() {
      this.preserveSearch = false;
    }
  }
};
</script>

<style scoped>
.auto-complete-link-to-item {
  color: #0078d4;
  cursor: pointer;
  margin-left: 1rem;
}
</style>
